<template>
  <div class="driver-block">
    <div class="driver-header">
      <div class="driver-info">
        <span class="driver-name">{{ driver.firstname }} {{ driver.surname }}</span>
        <span class="driver-email">{{ driver.email }}</span>
      </div>
      <div class="driver-actions">
        <span v-if="driver.mainDriver" class="main-driver-pill">Main Driver</span>
        <span class="driver-pill" :style="pillStyle">{{ isComplete ? 'Complete' : 'Incomplete' }}</span>
        <button class="driver-icon-btn" @click="isOpen = !isOpen" title="Edit">
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path
              d="M11.333 2.00004C11.5081 1.82494 11.716 1.68605 11.9447 1.59129C12.1735 1.49653 12.4187 1.44775 12.6663 1.44775C12.914 1.44775 13.1592 1.49653 13.388 1.59129C13.6167 1.68605 13.8246 1.82494 13.9997 2.00004C14.1748 2.17513 14.3137 2.383 14.4084 2.61178C14.5032 2.84055 14.552 3.08575 14.552 3.33337C14.552 3.581 14.5032 3.8262 14.4084 4.05497C14.3137 4.28374 14.1748 4.49161 13.9997 4.66671L4.99967 13.6667L1.33301 14.6667L2.33301 11L11.333 2.00004Z"
              stroke="currentColor" stroke-width="1.2" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
        </button>
        <button class="driver-icon-btn" @click="confirmAndDelete" title="Delete">
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M2 4H3.33333H14" stroke="currentColor" stroke-width="1.2" stroke-linecap="round" stroke-linejoin="round"/>
            <path
              d="M5.33301 4.00004V2.66671C5.33301 2.31309 5.47348 1.97395 5.72353 1.7239C5.97358 1.47385 6.31272 1.33337 6.66634 1.33337H9.33301C9.68663 1.33337 10.0258 1.47385 10.2758 1.7239C10.5259 1.97395 10.6663 2.31309 10.6663 2.66671V4.00004M12.6663 4.00004V13.3334C12.6663 13.687 12.5259 14.0261 12.2758 14.2762C12.0258 14.5262 11.6866 14.6667 11.333 14.6667H4.66634C4.31272 14.6667 3.97358 14.5262 3.72353 14.2762C3.47348 14.0261 3.33301 13.687 3.33301 13.3334V4.00004H12.6663Z"
              stroke="currentColor" stroke-width="1.2" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
        </button>
      </div>
    </div>
    <div v-if="!isComplete" class="driver-progress">
      <div class="driver-progress-track">
        <div
          class="driver-progress-bar"
          :style="{ width: clampedPercent + '%', backgroundColor: activeColor }"
        />
      </div>
      <span class="driver-progress-label" :style="{ color: activeColor }">{{ percent }}% complete</span>
    </div>
    <Transition @before-enter="onBeforeEnter" @enter="onEnter" @after-enter="onAfterEnter" @leave="onLeave">
      <div v-if="isOpen" class="driver-body">
        <driver-form :driver="driver" :product="product" @save="onSave" @delete="confirmAndDelete" @percent-updated="onPercentUpdated"/>
      </div>
    </Transition>
  </div>
</template>

<script setup>
import {ref, computed, onMounted, getCurrentInstance} from 'vue';
import DriverForm from './driver-form.vue';
import {EventBus} from "@/event-bus";
import {isTaxBandSelectionAllowed} from "@/helpers/utils";

const { proxy } = getCurrentInstance();

const props = defineProps({
  driver: {
    type: Object,
    required: true,
  },
  product: {
    type: Object,
    required: true,
  },
  color: {
    type: String,
    default: '#f69641',
  },
  completeColor: {
    type: String,
    default: '#a0c41c',
  },
  startOpen: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['driver-saved', 'driver-deleted', 'percent-updated']);

const isFilled = v => v !== null && v !== undefined && v !== '' && v !== false;

function computeDriverPercent(d) {
  const basic = [d.firstname, d.surname, d.email];
  if (props.product.display_settings_show_driving_license_main_driver) basic.push(d.drivingLicense);
  const employee = d.driverID ? [d.address, d.postcode, d.mobilePhone] : [];
  // A read-only (Jaama) tax band can't be set by the driver, so it doesn't count towards completion
  if (d.driverID && isTaxBandSelectionAllowed(props.product)) employee.push(d.taxBand);
  const allFields = [...basic, ...employee];
  const filled = allFields.filter(isFilled).length;
  return Math.round((filled / allFields.length) * 100);
}

const initialPercent = computeDriverPercent(props.driver);
const percent = ref(initialPercent);
const savedPercent = ref(initialPercent);

onMounted(() => {
  emit('percent-updated', percent.value);
});

function onPercentUpdated(val) {
  percent.value = val;
  // Only propagate to parent after a save — live form changes only update the local progress bar.
}

const isOpen = ref(props.startOpen);

const clampedPercent = computed(() => Math.min(100, Math.max(0, percent.value)));
const isComplete = computed(() => percent.value >= 100);
const activeColor = computed(() => isComplete.value ? props.completeColor : props.color);

const pillBgColor = computed(() => {
  const hex = activeColor.value.replace('#', '');
  const r = parseInt(hex.substring(0, 2), 16);
  const g = parseInt(hex.substring(2, 4), 16);
  const b = parseInt(hex.substring(4, 6), 16);
  return `rgba(${r}, ${g}, ${b}, 0.15)`;
});

const pillStyle = computed(() => ({
  color: activeColor.value,
  backgroundColor: pillBgColor.value,
}));

function onSave(submitPayload) {
  EventBus.$emit('loading', true);
  const messages = ['Saving Additional Driver', 'Recalculating quotes'];
  messages.forEach((message, index) => {
    setTimeout(() => EventBus.$emit('loadingTitle', message), index * 1000);
  });

  proxy.$http.post('/api/save-additional-driver', submitPayload)
    .then(response => {
      EventBus.$emit('loading', false);
      proxy.$notify({ group: 'notify', type: 'success', title: 'Success', text: 'Additional Driver saved successfully' });
      emit('driver-saved', response.data.data);
      savedPercent.value = percent.value;
      emit('percent-updated', percent.value);
      isOpen.value = false;
      EventBus.$emit('reload-driver-profile');
    })
    .catch(err => {
      EventBus.$emit('loading', false);
      proxy.$notify({ group: 'notify', type: 'error', title: 'Error', text: err.response.data.error.message });
    });
}

function onBeforeEnter(el) {
  el.style.height = '0';
  el.style.overflow = 'hidden';
}

function onEnter(el, done) {
  el.offsetHeight;
  el.style.transition = 'height 0.3s ease';
  el.style.height = el.scrollHeight + 'px';
  el.addEventListener('transitionend', done, { once: true });
}

function onAfterEnter(el) {
  el.style.height = 'auto';
  el.style.overflow = '';
  el.style.transition = '';
}

function onLeave(el, done) {
  el.style.overflow = 'hidden';
  el.style.height = el.scrollHeight + 'px';
  el.offsetHeight;
  el.style.transition = 'height 0.3s ease';
  el.style.height = '0';
  el.addEventListener('transitionend', done, { once: true });
}

function confirmAndDelete() {
  if (!confirm('Are you sure you want to delete this driver?')) return;

  if (!props.driver.additionalDriverID) {
    emit('driver-deleted');
    return;
  }

  deleteDriver();
}

function deleteDriver() {
  EventBus.$emit('loading', true);
  const messages = ['Deleting Additional Driver', 'Recalculating quotes'];
  messages.forEach((message, index) => {
    setTimeout(() => EventBus.$emit('loadingTitle', message), index * 1000);
  });

  proxy.$http.post('/api/delete-additional-driver', props.driver)
    .then(() => {
      EventBus.$emit('loading', false);

      proxy.$notify({
        group: 'notify',
        type: 'success',
        title: 'Success',
        text: 'Additional Driver deleted successfully'
      });

      emit('driver-deleted');

      EventBus.$emit('reload-driver-profile');
    })
    .catch(err => {
      EventBus.$emit('loading', false);

      proxy.$notify({
        group: 'notify',
        type: 'error',
        title: 'Error',
        text: err.response.data.error.message
      });
    });
}

</script>

<style scoped lang="scss">

</style>
